/**
 * ContactFormModal - Модальное окно для создания/редактирования контакта
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  TextInput,
  Textarea,
  Select,
  Button,
  Group,
  Stack,
  Text,
  Alert,
  ActionIcon,
  Checkbox,
  Divider,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconPlus,
  IconTrash,
  IconPhone,
  IconBrandTelegram,
  IconBrandWhatsapp,
  IconBriefcase,
} from '@tabler/icons-react';
import { normalizePhoneNumber, formatPhoneInput } from '../../../utils/phoneUtils';

// Типы телефонов
const PHONE_TYPES = [
  { value: 'mobile', label: 'Мобильный' },
  { value: 'landline', label: 'Городской' },
];

// Пустой телефон для добавления
const EMPTY_PHONE = { number: '', type: 'mobile' };

// Специальное значение для опции "Другое"
const OTHER_POSITION_VALUE = '__other__';

const ContactFormModal = ({
  opened,
  onClose,
  onSubmit,
  contact = null, // null = создание, object = редактирование
  organizationId,
  loading = false,
}) => {
  const isEditing = !!contact;

  const [formData, setFormData] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    position_id: null,
    position_custom: '',
    phones: [{ ...EMPTY_PHONE }],
    email: '',
    messengers: {
      telegram: '',
      whatsapp: '',
      viber: '',
    },
    is_primary: false,
    notes: '',
  });
  const [error, setError] = useState(null);
  const [positions, setPositions] = useState([]);
  const [showCustomPosition, setShowCustomPosition] = useState(false);

  // Загрузка должностей из справочника
  const loadPositions = useCallback(async () => {
    try {
      const response = await fetch('/api/partner-positions', {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setPositions(data || []);
      }
    } catch (err) {
      console.error('Failed to load positions:', err);
    }
  }, []);

  useEffect(() => {
    if (opened) {
      loadPositions();
    }
  }, [opened, loadPositions]);

  // Инициализация формы при открытии
  useEffect(() => {
    if (opened) {
      if (contact) {
        // Форматируем существующие номера телефонов
        const formattedPhones = contact.phones?.length > 0
          ? contact.phones.map(p => ({ ...p, number: formatPhoneInput(p.number) }))
          : [{ ...EMPTY_PHONE }];

        // Определяем, показывать ли кастомное поле
        const hasCustomPosition = contact.position_custom && !contact.position_id;

        setFormData({
          first_name: contact.first_name || '',
          middle_name: contact.middle_name || '',
          last_name: contact.last_name || '',
          position_id: contact.position_id || null,
          position_custom: contact.position_custom || '',
          phones: formattedPhones,
          email: contact.email || '',
          messengers: {
            telegram: contact.messengers?.telegram || '',
            whatsapp: contact.messengers?.whatsapp || '',
            viber: contact.messengers?.viber || '',
          },
          is_primary: contact.is_primary || false,
          notes: contact.notes || '',
        });
        setShowCustomPosition(hasCustomPosition);
      } else {
        setFormData({
          first_name: '',
          middle_name: '',
          last_name: '',
          position_id: null,
          position_custom: '',
          phones: [{ ...EMPTY_PHONE }],
          email: '',
          messengers: {
            telegram: '',
            whatsapp: '',
            viber: '',
          },
          is_primary: false,
          notes: '',
        });
        setShowCustomPosition(false);
      }
      setError(null);
    }
  }, [opened, contact]);

  // Обработка изменения должности
  const handlePositionChange = (value) => {
    if (value === OTHER_POSITION_VALUE) {
      setShowCustomPosition(true);
      setFormData({ ...formData, position_id: null, position_custom: '' });
    } else {
      setShowCustomPosition(false);
      setFormData({ ...formData, position_id: value ? parseInt(value) : null, position_custom: '' });
    }
  };

  // Получить значение для Select
  const getPositionSelectValue = () => {
    if (showCustomPosition) return OTHER_POSITION_VALUE;
    if (formData.position_id) return formData.position_id.toString();
    return null;
  };

  // Формируем данные для Select
  const positionOptions = [
    ...positions.map(p => ({ value: p.id.toString(), label: p.name })),
    { value: OTHER_POSITION_VALUE, label: 'Другое...' },
  ];

  // Добавить телефон
  const addPhone = () => {
    if (formData.phones.length < 10) {
      setFormData({
        ...formData,
        phones: [...formData.phones, { ...EMPTY_PHONE }],
      });
    }
  };

  // Удалить телефон
  const removePhone = (index) => {
    const newPhones = formData.phones.filter((_, i) => i !== index);
    // Оставляем минимум одно пустое поле
    if (newPhones.length === 0) {
      newPhones.push({ ...EMPTY_PHONE });
    }
    setFormData({ ...formData, phones: newPhones });
  };

  // Обновить телефон
  const updatePhone = (index, field, value) => {
    const newPhones = [...formData.phones];
    // Форматируем номер на лету при вводе
    const formattedValue = field === 'number' ? formatPhoneInput(value) : value;
    newPhones[index] = { ...newPhones[index], [field]: formattedValue };
    setFormData({ ...formData, phones: newPhones });
  };

  // Обновить мессенджер
  const updateMessenger = (key, value) => {
    setFormData({
      ...formData,
      messengers: { ...formData.messengers, [key]: value },
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Валидация
    if (!formData.first_name.trim()) {
      setError('Имя обязательно');
      return;
    }

    try {
      // Фильтруем пустые телефоны и нормализуем номера (8 -> +7)
      const phones = formData.phones
        .filter((p) => p.number.trim())
        .map((p) => ({ number: normalizePhoneNumber(p.number.trim()), type: p.type }));

      // Фильтруем пустые мессенджеры
      const messengers = {};
      if (formData.messengers.telegram?.trim()) {
        messengers.telegram = formData.messengers.telegram.trim();
      }
      if (formData.messengers.whatsapp?.trim()) {
        messengers.whatsapp = formData.messengers.whatsapp.trim();
      }
      if (formData.messengers.viber?.trim()) {
        messengers.viber = formData.messengers.viber.trim();
      }

      const data = {
        organization_id: organizationId,
        first_name: formData.first_name.trim(),
        middle_name: formData.middle_name?.trim() || null,
        last_name: formData.last_name?.trim() || null,
        position_id: formData.position_id || null,
        position_custom: showCustomPosition ? (formData.position_custom?.trim() || null) : null,
        phones: phones.length > 0 ? phones : null,
        email: formData.email?.trim() || null,
        messengers: Object.keys(messengers).length > 0 ? messengers : null,
        is_primary: formData.is_primary,
        notes: formData.notes?.trim() || null,
      };

      await onSubmit(data);
    } catch (err) {
      setError(err.message || 'Ошибка при сохранении контакта');
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEditing ? 'Редактировать контакт' : 'Добавить контакт'}
      size="lg"
      radius="xl"
      styles={{
        content: {
          borderRadius: '16px',
        },
      }}
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
              {error}
            </Alert>
          )}

          {/* ФИО */}
          <Group grow>
            <TextInput
              label="Фамилия"
              placeholder="Иванов"
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
            />
            <TextInput
              label="Имя"
              placeholder="Иван"
              required
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
            />
            <TextInput
              label="Отчество"
              placeholder="Иванович"
              value={formData.middle_name}
              onChange={(e) => setFormData({ ...formData, middle_name: e.target.value })}
            />
          </Group>

          {/* Должность */}
          <Select
            label="Должность"
            placeholder="Выберите должность"
            data={positionOptions}
            value={getPositionSelectValue()}
            onChange={handlePositionChange}
            leftSection={<IconBriefcase size={16} />}
            clearable
            searchable
          />

          {/* Кастомная должность (если выбрано "Другое") */}
          {showCustomPosition && (
            <TextInput
              label="Укажите должность"
              placeholder="Введите название должности"
              value={formData.position_custom}
              onChange={(e) => setFormData({ ...formData, position_custom: e.target.value })}
              leftSection={<IconBriefcase size={16} />}
            />
          )}

          <Divider label="Телефоны" labelPosition="left" />

          {/* Телефоны */}
          <Stack gap="xs">
            {formData.phones.map((phone, index) => (
              <Group key={index} gap="xs" align="flex-end">
                <TextInput
                  style={{ flex: 1 }}
                  placeholder="+7 (999) 123-45-67"
                  leftSection={<IconPhone size={16} />}
                  value={phone.number}
                  onChange={(e) => updatePhone(index, 'number', e.target.value)}
                />
                <Select
                  w={140}
                  data={PHONE_TYPES}
                  value={phone.type}
                  onChange={(value) => updatePhone(index, 'type', value)}
                />
                <ActionIcon
                  variant="subtle"
                  color="red"
                  onClick={() => removePhone(index)}
                  disabled={formData.phones.length === 1 && !phone.number}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            ))}

            {formData.phones.length < 10 && (
              <Button
                variant="subtle"
                size="xs"
                leftSection={<IconPlus size={14} />}
                onClick={addPhone}
                style={{ alignSelf: 'flex-start' }}
              >
                Добавить телефон
              </Button>
            )}

            <Text size="xs" c="dimmed">
              Можно добавить до 10 телефонов
            </Text>
          </Stack>

          <Divider label="Контакты" labelPosition="left" />

          {/* Email */}
          <TextInput
            label="Email"
            placeholder="contact@example.com"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />

          {/* Мессенджеры */}
          <Group grow>
            <TextInput
              label="Telegram"
              placeholder="@username или +79991234567"
              leftSection={<IconBrandTelegram size={16} />}
              value={formData.messengers.telegram}
              onChange={(e) => updateMessenger('telegram', e.target.value)}
            />
            <TextInput
              label="WhatsApp"
              placeholder="+79991234567"
              leftSection={<IconBrandWhatsapp size={16} />}
              value={formData.messengers.whatsapp}
              onChange={(e) => updateMessenger('whatsapp', e.target.value)}
            />
          </Group>

          <TextInput
            label="Viber"
            placeholder="+79991234567"
            value={formData.messengers.viber}
            onChange={(e) => updateMessenger('viber', e.target.value)}
          />

          <Divider label="Дополнительно" labelPosition="left" />

          {/* Заметки */}
          <Textarea
            label="Комментарии"
            placeholder="Дополнительная информация о контакте..."
            minRows={2}
            maxRows={4}
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          />

          {/* Основной контакт */}
          <Checkbox
            label="Основной контакт организации"
            description="Будет отображаться первым в списке"
            checked={formData.is_primary}
            onChange={(e) => setFormData({ ...formData, is_primary: e.currentTarget.checked })}
          />

          {/* Кнопки */}
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={onClose}>
              Отмена
            </Button>
            <Button type="submit" loading={loading}>
              {isEditing ? 'Сохранить' : 'Добавить'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default ContactFormModal;
